import React, { useMemo } from 'react'
import { createPortal } from 'react-dom'

import type * as TooltipTypes from './types'

import styles from './styles.module.css'

export { useSpendTooltip } from './hooks/useSpendTooltip'
export type { TooltipItem, TooltipData } from './types'

interface SpendTooltipProps {
  data: TooltipTypes.TooltipData | null
  position: TooltipTypes.Position
  tooltipRef: React.RefObject<HTMLDivElement>
  currency?: string
}

export const SpendTooltip: React.FC<SpendTooltipProps> = ({
  data,
  position,
  tooltipRef,
  currency = 'BRL'
}) => {
  const fmt = useMemo(
    () => new Intl.NumberFormat('pt-BR', { style: 'currency', currency }),
    [currency]
  )
  const percentFmt = useMemo(
    () => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }),
    []
  )

  const formatValue = (item: TooltipTypes.TooltipData['items'][number]) => {
    if (item.value == null) return '—'
    if (item.format === 'percent') return `${percentFmt.format(item.value)}%`
    return fmt.format(item.value)
  }

  if (!data) return null

  return createPortal(
    <div
      ref={tooltipRef}
      className={styles.tooltip}
      style={{ left: position.left, top: position.top }}
      aria-hidden="true"
    >
      <div className={styles.title}>{data.title}</div>

      {data.items.map(item => (
        <div key={item.datasetLabel} className={styles.row}>
          {item.dashed ? (
            <svg
              width="16"
              height="4"
              viewBox="0 0 16 4"
              aria-hidden="true"
              focusable="false"
              style={{ flexShrink: 0 }}
            >
              <line
                x1="0"
                y1="2"
                x2="16"
                y2="2"
                stroke={item.color}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
            </svg>
          ) : (
            <span
              className={styles.dot}
              style={{ backgroundColor: item.color }}
            />
          )}
          <span className={styles.label}>{item.datasetLabel}</span>
          <span className={styles.value}>{formatValue(item)}</span>
        </div>
      ))}

      {data.forecastRange && (
        <div className={styles.forecastBand}>
          <div className={styles.bandLabel}>Intervalo de previsão</div>
          <div className={styles.bandValues}>
            <span>{fmt.format(data.forecastRange.low)}</span>
            <span> – </span>
            <span>{fmt.format(data.forecastRange.high)}</span>
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}
